import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { useChangeRole } from '@/hooks/useTeam'
import { Loader2, Shield, User } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import type { TeamMember } from '@/hooks/useTeam'

interface ChangeRoleModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  member: TeamMember | null
}

export function ChangeRoleModal({ open, onOpenChange, member }: ChangeRoleModalProps) {
  const [newRole, setNewRole] = useState<'admin' | 'member'>('member')
  const { toast } = useToast()
  const changeRoleMutation = useChangeRole()

  useEffect(() => {
    if (member) {
      setNewRole(member.role as 'admin' | 'member')
    }
  }, [member])

  const handleChangeRole = async () => {
    if (!member) return

    // No change
    if (newRole === member.role) {
      onOpenChange(false)
      return
    }

    try {
      await changeRoleMutation.mutateAsync({
        memberId: member.id,
        role: newRole
      })

      toast({
        title: 'Role updated',
        description: `${member.user.full_name}'s role has been changed to ${newRole}`,
      })

      onOpenChange(false)
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to change role'
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  if (!member) return null

  const getRoleBadgeColor = (role: string) => {
    return role === 'admin' 
      ? 'bg-blue-100 text-blue-800 border-blue-200'
      : 'bg-gray-100 text-gray-800 border-gray-200'
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Change Member Role</DialogTitle>
          <DialogDescription>
            Update {member.user.full_name}'s permissions and access level
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Current Role */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Current Role</Label>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={getRoleBadgeColor(member.role)}>
                {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
              </Badge>
            </div>
          </div>

          {/* New Role Selection */}
          <div className="space-y-3">
            <Label>New Role</Label>
            <div className="space-y-2">
              <div
                className={`flex items-start space-x-3 rounded-md border p-3 cursor-pointer transition-colors ${
                  newRole === 'member' ? 'border-primary bg-primary/5' : 'hover:border-gray-300'
                }`}
                onClick={() => setNewRole('member')}
              >
                <input
                  type="radio"
                  id="role-member"
                  value="member"
                  checked={newRole === 'member'}
                  onChange={(e) => setNewRole(e.target.value as 'admin' | 'member')}
                  disabled={changeRoleMutation.isPending}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <Label htmlFor="role-member" className="font-medium cursor-pointer">
                      Member
                    </Label>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Can access and use all knowledge bases
                  </p>
                </div>
              </div>

              <div
                className={`flex items-start space-x-3 rounded-md border p-3 cursor-pointer transition-colors ${
                  newRole === 'admin' ? 'border-primary bg-primary/5' : 'hover:border-gray-300'
                }`}
                onClick={() => setNewRole('admin')}
              >
                <input
                  type="radio"
                  id="role-admin"
                  value="admin"
                  checked={newRole === 'admin'}
                  onChange={(e) => setNewRole(e.target.value as 'admin' | 'member')}
                  disabled={changeRoleMutation.isPending}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <Label htmlFor="role-admin" className="font-medium cursor-pointer">
                      Admin
                    </Label>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Can invite members and manage team
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={changeRoleMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleChangeRole}
            disabled={changeRoleMutation.isPending || newRole === member.role}
          >
            {changeRoleMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
